/**
 * 📜 MODULAR QUEST SYSTEM
 *
 * Quest Categories:
 * - MAIN QUESTS: Linear story progression (chapters 1-10)
 * - SIDE QUESTS: Optional parallel objectives unlocked by progression
 * - EXTRA QUESTS: One-time achievements and challenges
 *
 * Each category can be expanded independently.
 */

// Import quest definitions
import mainQuests, { chapters as mainChapters } from './mainQuests';
import sideQuests, { sideQuestChains } from './sideQuests';
import extraQuests, { extraQuestCategories } from './extraQuests';

// ═══════════════════════════════════════════════════════════════════════════
// QUEST CATEGORIES
// ═══════════════════════════════════════════════════════════════════════════

export const QUEST_CATEGORIES = {
  MAIN: 'main',
  SIDE: 'side',
  EXTRA: 'extra',
};

// ═══════════════════════════════════════════════════════════════════════════
// COMBINED QUEST ACCESS (for backward compatibility)
// ═══════════════════════════════════════════════════════════════════════════

// Legacy export - all quests combined (main quests only for backward compat)
export const quests = mainQuests;

// All quests by category
export const allQuests = {
  main: mainQuests,
  side: sideQuests,
  extra: extraQuests,
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN QUEST HELPERS
// ═══════════════════════════════════════════════════════════════════════════

export const chapters = mainChapters;

export const getMainQuestIds = () => Object.keys(mainQuests);
export const getMainQuestCount = () => Object.keys(mainQuests).length;
export const getMainQuest = (id) => mainQuests[id];

export const getMainQuestsByChapter = (chapter) =>
  Object.values(mainQuests).filter(q => q.chapter === chapter);

export const getMainChapterProgress = (completedQuests, chapter) => {
  const chapterQuests = getMainQuestsByChapter(chapter);
  const completed = chapterQuests.filter(q => completedQuests.includes(q.id));
  return { completed: completed.length, total: chapterQuests.length };
};

// Legacy aliases
export const getQuestIds = getMainQuestIds;
export const getQuestCount = getMainQuestCount;
export const getQuest = getMainQuest;
export const getQuestsByChapter = getMainQuestsByChapter;
export const getChapterProgress = getMainChapterProgress;

// ═══════════════════════════════════════════════════════════════════════════
// SIDE QUEST HELPERS
// ═══════════════════════════════════════════════════════════════════════════

export { sideQuestChains };

export const getSideQuestIds = () => Object.keys(sideQuests);
export const getSideQuestCount = () => Object.keys(sideQuests).length;
export const getSideQuest = (id) => sideQuests[id];

export const getSideQuestsByChain = (chainId) =>
  Object.values(sideQuests).filter(q => q.chain === chainId);

export const getSideChainProgress = (completedQuests, chainId) => {
  const chainQuests = getSideQuestsByChain(chainId);
  const completed = chainQuests.filter(q => completedQuests.includes(q.id));
  return { completed: completed.length, total: chainQuests.length };
};

export const getAvailableSideQuests = (completedQuests, completedMainQuests, playerState) => {
  return Object.values(sideQuests).filter(quest => {
    // Already completed
    if (completedQuests.includes(quest.id)) return false;

    // Check unlock requirements
    if (quest.unlockRequirements) {
      const req = quest.unlockRequirements;

      // Requires main quest completion
      if (req.mainQuest && !completedMainQuests.includes(req.mainQuest)) return false;

      // Requires chapter unlocked
      if (req.chapter && playerState.currentChapter < req.chapter) return false;

      // Requires previous side quest in chain
      if (req.sideQuest && !completedQuests.includes(req.sideQuest)) return false;

      // Requires tech
      if (req.tech && !playerState.researchedTechs?.includes(req.tech)) return false;

      // Requires building
      if (req.building && (playerState.buildingCounts?.[req.building] || 0) < (req.buildingCount || 1)) return false;
    }

    return true;
  });
};

// ═══════════════════════════════════════════════════════════════════════════
// EXTRA QUEST HELPERS
// ═══════════════════════════════════════════════════════════════════════════

export { extraQuestCategories };

export const getExtraQuestIds = () => Object.keys(extraQuests);
export const getExtraQuestCount = () => Object.keys(extraQuests).length;
export const getExtraQuest = (id) => extraQuests[id];

export const getExtraQuestsByCategory = (category) =>
  Object.values(extraQuests).filter(q => q.category === category);

export const getExtraCategoryProgress = (completedQuests, category) => {
  const categoryQuests = getExtraQuestsByCategory(category);
  const completed = categoryQuests.filter(q => completedQuests.includes(q.id));
  return { completed: completed.length, total: categoryQuests.length };
};

export const getAvailableExtraQuests = (completedQuests, playerState) => {
  return Object.values(extraQuests).filter(quest => {
    // Already completed (extra quests are one-time)
    if (completedQuests.includes(quest.id)) return false;

    // Check if hidden until certain progress
    if (quest.hiddenUntil) {
      const req = quest.hiddenUntil;
      if (req.tilesExplored && playerState.tilesExplored < req.tilesExplored) return false;
      if (req.buildingsOwned && playerState.totalBuildings < req.buildingsOwned) return false;
      if (req.dungeonClears && playerState.dungeonClears < req.dungeonClears) return false;
    }

    return true;
  });
};

// ═══════════════════════════════════════════════════════════════════════════
// UNIVERSAL QUEST HELPERS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get any quest by ID (searches all categories)
 */
export const getAnyQuest = (id) => {
  return mainQuests[id] || sideQuests[id] || extraQuests[id] || null;
};

/**
 * Get quest category by ID
 */
export const getQuestCategory = (id) => {
  if (mainQuests[id]) return QUEST_CATEGORIES.MAIN;
  if (sideQuests[id]) return QUEST_CATEGORIES.SIDE;
  if (extraQuests[id]) return QUEST_CATEGORIES.EXTRA;
  return null;
};

/**
 * Get total quest counts
 */
export const getTotalQuestCounts = () => ({
  main: getMainQuestCount(),
  side: getSideQuestCount(),
  extra: getExtraQuestCount(),
  total: getMainQuestCount() + getSideQuestCount() + getExtraQuestCount(),
});

/**
 * Get completion stats
 */
export const getCompletionStats = (completedMain, completedSide, completedExtra) => ({
  main: { completed: completedMain.length, total: getMainQuestCount() },
  side: { completed: completedSide.length, total: getSideQuestCount() },
  extra: { completed: completedExtra.length, total: getExtraQuestCount() },
  total: {
    completed: completedMain.length + completedSide.length + completedExtra.length,
    total: getMainQuestCount() + getSideQuestCount() + getExtraQuestCount(),
  },
});

// Re-export everything
export { mainQuests, sideQuests, extraQuests };

export default {
  quests,
  allQuests,
  mainQuests,
  sideQuests,
  extraQuests,
  QUEST_CATEGORIES,
  chapters,
  sideQuestChains,
  extraQuestCategories,
};
